import {
  useCreateScheduledReview as useCreateScheduledReviewMutation,
  Variables,
  Data,
} from '../../hooks/queries/scheduledReviews/useCreateScheduledReview'
import { InformalFeedbackRequest } from '../../api/clients/InformalFeedbackRequestsClient'
import { SubmitVariables } from './useSubmit'
import { nextOccurrenceFrom } from './ReviewersSelection/NextOccurrence'
import { ReviewMember } from '../../types'
import { RecipientsSelectorValueType } from 'types'
import { ChannelValue } from 'components/ShareSheet/RecipientSelector/Types'
import dayjs from 'dayjs'
import { ReviewPrivacy } from './types'
import { MutateOptions } from 'react-query'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

export const useCreateScheduledReview = (options?: MutateOptions<Data, unknown, Variables>) => {
  const { mutateAsync: createScheduledReview } = useCreateScheduledReviewMutation()

  return (request: InformalFeedbackRequest, variables: SubmitVariables) => {
    const { interval, useReviewLayer, recipients, approvers, viewers, frequency, weekdays } = variables

    if (!interval) return

    const nextOccurrence = nextOccurrenceFrom(request.created_at, interval, weekdays)

    if (!nextOccurrence) return

    const members: ReviewMember[] = []

    request.reviews.forEach((review) => {
      const isReviewerFromUser =
        recipients.find(
          (recipient) =>
            recipient.type === RecipientsSelectorValueType.User && recipient.value.id === review.reviewer.id,
        ) !== undefined
      // it's possible this review was created from a channel, in which case we don't want to add a user member
      if (isReviewerFromUser) {
        members.push({
          id: review.reviewer.id,
          membership_type: review.required ? 'approver' : 'reviewer',
          type: 'User',
        })
      }
    })

    request.viewers.forEach((viewer) => {
      const isViewerFromUser =
        viewers.find(
          (recipient) => recipient.type === RecipientsSelectorValueType.User && recipient.value.id === viewer.viewer.id,
        ) !== undefined
      // it's possible this viewer was created from a channel, in which case we don't want to add a user member
      if (isViewerFromUser) {
        members.push({
          id: viewer.viewer.id,
          membership_type: 'viewer',
          type: 'User',
        })
      }
    })

    const channelReviewerRecipients = recipients.filter(
      (recipient) => recipient.type === RecipientsSelectorValueType.Channel,
    ) as ChannelValue[]

    channelReviewerRecipients.forEach((channelRecipient) => {
      const isApprover =
        approvers.find(
          (approver) =>
            approver.type === RecipientsSelectorValueType.Channel && approver.value.id === channelRecipient.value.id,
        ) !== undefined
      members.push({
        id: channelRecipient.value.id,
        membership_type: isApprover ? 'approver' : 'reviewer',
        type: 'Channel',
      })
    })

    const channelViewerRecipients = viewers.filter(
      (viewer) => viewer.type === RecipientsSelectorValueType.Channel,
    ) as ChannelValue[]

    channelViewerRecipients.forEach((channelRecipient) => {
      members.push({
        id: channelRecipient.value.id,
        membership_type: 'viewer',
        type: 'Channel',
      })
    })

    const intervalDuration = dayjs.duration(interval)

    return createScheduledReview(
      {
        article_id: request.article.key,
        name: request.title,
        first_occurrence: nextOccurrence.toISOString(),
        members,
        email_members: [],
        duration: Math.min(dayjs(request.ends_on).diff(dayjs(request.created_at), 'day'), intervalDuration.days()),
        interval,
        privacy: useReviewLayer ? ReviewPrivacy.PRIVATE : ReviewPrivacy.PUBLIC,
        description: request.description,
        html_description: request.html_description,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        frequency: frequency!,
        weekdays,
      },
      options,
    )
  }
}
